import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	FormattedCurrency,
	IDataListColumn,
	IconButton,
	Id,
	Loading,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
	useIsAddonEnabled,
	useOpen,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { ContactFilterApplyDrawer, ContactFilterBar } from "../../../contactFilter";
import { ContactBulkEditAction } from "../../../contacts/ContactBulkEditAction";
import { useContactListUtils } from "../../../contacts/useContactListUtils";
import { LocalContactViewSwitcher } from "../../../contactViews/local/LocalContactViewSwitcher";
import { CostDrawer } from "../../../events/eventDetails/eventContacts/CostDrawer";
import { EventContactExportButton } from "../../../events/eventDetails/eventContacts/EventContactExportButton";
import { IEventContactEndpoints } from "../../../shared/api/event/withEventContactEndpoints";
import { useContactView } from "../../../shared/hooks/useContactView";
import { IEventContactIndexResource } from "../../../shared/types";

interface Props {
	eventId: Id;
	eventContactService: IEventContactEndpoints;
	onRemove?: (contact: IEventContactIndexResource) => void;
	onAdd?: () => void;
	onChangeStatus?: (status: IEventContactIndexResource) => void;
}

export const EventContactList: FC<Props> = ({ eventId, onAdd, onRemove, onChangeStatus, eventContactService }) => {
	const isPressModuleEnabled = useIsAddonEnabled("pressModule");
	const [costDrawerContact, setCostDrawerContact] = useState<IEventContactIndexResource | null>(null);
	const costDrawer = useOpen();
	const { t } = useTranslation();
	const { currentContactView, isLoading: contactViewLoading } = useContactView();
	const navigate = useNavigate();
	const { columns } = useContactListUtils(currentContactView);
	const applyFilterDrawerProps = useEditDrawer();
	const [searchParams, setSearchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const activeContactFilterId = useMemo(() => {
		const id = searchParams.get("contactFilterId");
		return id ? +id : null;
	}, [searchParams]);

	const onApplyContactFilter = useCallback(
		(id: Id | null) => {
			if (id) {
				setSearchParams((prev) => {
					prev.set("contactFilterId", `${id}`);
					return prev;
				});
			} else {
				setSearchParams((prev) => {
					prev.delete("contactFilterId");
					return prev;
				});
			}
			void queryClient.invalidateQueries({ queryKey: eventContactService.contactsKey(eventId) });
		},
		[queryClient, eventContactService, eventId, setSearchParams],
	);

	const dataListColumns: IDataListColumn<IEventContactIndexResource>[] =
		useMemo((): IDataListColumn<IEventContactIndexResource>[] => {
			const pressModuleColumns: IDataListColumn<IEventContactIndexResource>[] = isPressModuleEnabled
				? [
						{
							heading: t("Cost"),
							type: "value",
							size: "max-content",
							valueKey: "cost",
							render: (eventContact) => <FormattedCurrency value={eventContact.cost} />,
						},
						{
							heading: t("Clippings"),
							type: "value",
							size: "max-content",
							valueKey: "numberOfClippings",
						},
					]
				: [];

			return [
				{
					heading: t("Status"),
					type: "value",
					size: "max-content",
					valueKey: "status",
					render: (value) => (value.status ? t(value.status) : "-"),
					sortable: true,
				},
				...pressModuleColumns,
				...(columns as unknown as IDataListColumn<IEventContactIndexResource>[]),
				{
					heading: "",
					type: "actions",
					size: "max-content",
					action: (contact) => (
						<ContextMenu displayShortcutsAs="icon">
							{isPressModuleEnabled && (
								<ContextMenuItem
									action={() => {
										setCostDrawerContact(contact);
										costDrawer.handleOpen();
									}}
									shortcut={true}
									title={t("Update cost")}
									label={t("Update cost")}
									icon="edit"
								/>
							)}
							{onChangeStatus && (
								<ContextMenuItem
									shortcut={true}
									action={() => onChangeStatus(contact)}
									title={t("Change Status")}
									label={t("Change Status")}
									icon="checklist"
								/>
							)}
							<ContextMenuLinkItem
								to={`/contacts/${contact.id}`}
								title={t("View contact")}
								label={t("View contact")}
								icon="eye"
								shortcut="primary"
							/>
							{onRemove && (
								<ContextMenuDeleteItem
									action={() => onRemove(contact)}
									title={t("Remove contact")}
									label={t("Remove contact")}
									heading={t("Remove")}
									labels={{ close: t("Close"), confirm: t("Confirm") }}
								>
									{t("Are you sure you want to remove this contact?")}
								</ContextMenuDeleteItem>
							)}
						</ContextMenu>
					),
				},
			];
		}, [columns, costDrawer, isPressModuleEnabled, onRemove, onChangeStatus, t]);

	const openContactFilterDrawer = useCallback(() => {
		applyFilterDrawerProps.handleOpen(activeContactFilterId || "new");
	}, [activeContactFilterId, applyFilterDrawerProps]);

	if (contactViewLoading) {
		return <Loading />;
	}

	return (
		<>
			<PaginatedAutoDataList
				name="eventContacts"
				empty={t("No contact available.")}
				headerChildren={
					<>
						<ContactFilterBar
							contactFilterId={activeContactFilterId}
							onApply={onApplyContactFilter}
							onClick={openContactFilterDrawer}
						/>
					</>
				}
				heading={t("Contacts")}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					[...eventContactService.contactsKey(eventId), String(activeContactFilterId)],
					(config: PaginatedListFetcherConfig<IEventContactIndexResource>) =>
						eventContactService.contacts(eventId, activeContactFilterId, config),
				)}
				headerActions={
					<>
						<LocalContactViewSwitcher />
						{onAdd && <IconButton size="small" title={t("Add contact")} icon="plus" color="primary" onClick={onAdd} />}
						<EventContactExportButton
							eventContactService={eventContactService}
							eventId={eventId}
							viewId={currentContactView?.id}
							filterId={activeContactFilterId}
						/>
						<IconButton size="small" title={t("Contact filter")} icon="filter" onClick={openContactFilterDrawer} />
					</>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
					],
				}}
				onRowClick={({ id }) => navigate(`/contacts/${id}`)}
				bulkEditActions={<ContactBulkEditAction contactFilterId={activeContactFilterId} />}
				enableBulkEdit={true}
			/>
			{applyFilterDrawerProps.editEntityId != null && (
				<ContactFilterApplyDrawer
					{...applyFilterDrawerProps}
					contactFilterId={activeContactFilterId}
					onApply={onApplyContactFilter}
				/>
			)}
			{costDrawerContact && (
				<CostDrawer
					eventId={eventId}
					eventContactService={eventContactService}
					open={costDrawer.open}
					contactId={costDrawerContact.id}
					cost={costDrawerContact.cost}
					onClose={costDrawer.handleClose}
					onClosed={() => setCostDrawerContact(null)}
				/>
			)}
		</>
	);
};
